import type { ReactNode } from "react";
import { useId, useState } from "react";
import type { Subscription, UsageHistoryEntry } from "api";
import cn from "classnames";
import { formatMonth } from "utils/date.utils";
import { formatCount, usagePercent } from "utils/number.utils";

type UsageBlockProps = {
  subscription: Subscription;
  /** Where "Buy More Calls" goes; the button is left out without one. */
  buyUrl?: string;
  /** Drawn between the meters and the buttons — the limit-reached card. */
  notice?: ReactNode;
};

/**
 * Months before `history_since` were never recorded, so nothing is drawn for
 * them — an empty month there would read as a quiet one.
 */
export const recordedHistory = (subscription: Subscription): UsageHistoryEntry[] => {
  const since = subscription.history_since;

  return since === null ? subscription.history : subscription.history.filter((entry) => entry.period >= since);
};

type MeterProps = {
  used: number;
  limit: number | null;
  remaining: number | null;
  percent: number | null;
  /** Reads after the counter: "of 60,000 calls per month". */
  label: string;
  /** The second meter is separated by a rule, as in the prototype. */
  second?: boolean;
};

/** One counter and its bar. Unlimited tools have nothing to fill, so no bar. */
const Meter = ({ used, limit, remaining, percent, label, second = false }: MeterProps) => {
  const exhausted = limit !== null && remaining === 0;

  return (
    <>
      <div className={cn("pc-use__nums", { "pc-use__nums--2": second })}>
        <b>{formatCount(used)}</b>
        <span>{label}</span>
        {limit !== null && <em className={cn({ "is-bad": exhausted })}>{formatCount(remaining ?? 0)} left</em>}
      </div>

      {limit !== null && (
        <div className={cn("pc-bar", { "pc-bar--out": exhausted })}>
          <span style={{ width: `${usagePercent(percent)}%` }} />
        </div>
      )}
    </>
  );
};

type HistoryRowProps = {
  label: string;
  used: number;
  percent: number | null;
};

const HistoryRow = ({ label, used, percent }: HistoryRowProps) => (
  <div className="pc-hist__row">
    <span>{label}</span>
    <b>{formatCount(used)}</b>
    <i className="pc-hist__bar">
      <span style={{ width: `${usagePercent(percent)}%` }} />
    </i>
    <em>{percent === null ? "—" : `${percent}%`}</em>
  </div>
);

/**
 * Used / limit / left, the fill bar and the month history behind a toggle —
 * the block every subscription row shows.
 *
 * Tracking tools are capped twice, on calls and on distinct cargo numbers, so
 * they get a second meter and a history grouped by month rather than a flat
 * one-line-per-month list.
 */
export const UsageBlock = ({ subscription, buyUrl, notice }: UsageBlockProps) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyId = useId();

  const { monthly_limit: limit, monthly_unique_limit: uniqueLimit, usage } = subscription;
  const history = recordedHistory(subscription);
  const hasUnique = uniqueLimit !== null && usage.unique_used !== null;

  const renderHistoryEntry = (entry: UsageHistoryEntry) =>
    hasUnique ? (
      <div className="pc-hist__m" key={entry.period}>
        <div className="pc-hist__mn">{formatMonth(entry.period)}</div>
        <HistoryRow label="Calls" used={entry.used} percent={entry.percent} />
        <HistoryRow label="Unique shipments" used={entry.unique_used ?? 0} percent={entry.unique_percent} />
      </div>
    ) : (
      <HistoryRow key={entry.period} label={formatMonth(entry.period)} used={entry.used} percent={entry.percent} />
    );

  return (
    <>
      <Meter
        used={usage.used}
        limit={limit}
        remaining={usage.remaining}
        percent={usage.percent}
        label={limit === null ? "calls this month" : `of ${formatCount(limit)} calls per month`}
      />

      {hasUnique && (
        <Meter
          second
          used={usage.unique_used ?? 0}
          limit={uniqueLimit}
          remaining={usage.unique_remaining}
          percent={usage.unique_percent}
          label={`of ${formatCount(uniqueLimit)} unique shipments`}
        />
      )}

      {notice}

      <div className="pc-use__foot">
        <button
          type="button"
          className={cn("pcbtn pc-use__hist", { "is-copied": historyOpen })}
          aria-expanded={historyOpen}
          aria-controls={historyOpen ? historyId : undefined}
          onClick={() => setHistoryOpen((current) => !current)}
        >
          {historyOpen ? "Hide history" : "History"}
        </button>
        {buyUrl !== undefined && (
          <a className="pcbtn pcbtn--go pc-use__buy" href={buyUrl} target="_blank" rel="noopener noreferrer">
            Buy More Calls
          </a>
        )}
      </div>

      {historyOpen && (
        <div className="pc-hist" id={historyId}>
          {history.length === 0 ? (
            <p className="pc-hist__none">No closed months yet — this is the first period.</p>
          ) : (
            history.map(renderHistoryEntry)
          )}
        </div>
      )}
    </>
  );
};
